import React, { createContext, ReactNode, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRawLaunchParams } from '@telegram-apps/sdk-react';
import { useTranslation } from 'react-i18next';

import { routes } from '@/navigation/routes.tsx';
import LoginController from '@/api/login/login.ts';
import ProfileController from '@/api/profile/profile.ts';
import { PaywallTypes, Products, RelationKind } from '@/enums/apiEnums.ts';
import centrifuge from '@/ws/config.ts';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { SUBSCRIPTION_GROUPS } from '@/constants/subscription_groups.ts';
import { OFFERING_IDS } from '@/constants/offering_ids.ts';
import { useSummariesStore } from '@/store/useSummariesStore.ts';
import { catchErrors, logError, logEvent } from '@/utils/sentry-monitor.tsx';
import { helpers } from '@/helpers.ts';
import { useAuthStore } from '@/store/useAuthStore.ts';

interface AuthContextType {
  isAuthenticated: boolean;
  isFetching: boolean;
  isCentrifuge: boolean;
  login: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isCentrifuge, setIsCentrifuge] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const { setAboutMe, setRemainingAmount, updatePremium } = useProfileStore();
  const { setLikesCount } = useSummariesStore();
  const { setTokens, clearTokens } = useAuthStore();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const launchParamsRaw = useRawLaunchParams();
  const params = new URLSearchParams(launchParamsRaw);
  const tgWebAppData = params.get('tgWebAppData') ?? '';

  const login = async () => {
    const loginId = helpers.generateRequestId();

    setIsFetching(true);
    try {
      const {
        token_bag: { access_token, refresh_token, access_expire }
      } = await LoginController.telegramConnect({
        initData: tgWebAppData,
        product_id: Products.kyiv_date
      });

      setTokens({
        accessToken: access_token,
        refreshToken: refresh_token,
        accessExpire: access_expire
      });

      connectCentrifuge(access_token);

      const user = await ProfileController.initProfile({
        offering_ids: [OFFERING_IDS.giftId, OFFERING_IDS.premiumId]
      });

      const premium = user.subscription_purchases?.find(
        (p) =>
          p.product.type === PaywallTypes.TYPE_SUBSCRIPTION &&
          p.product.subscription_details?.group === SUBSCRIPTION_GROUPS.premium
      );

      const likesCount =
        user.relation_summaries?.find(
          (r) => r.kind === RelationKind.KIND_LIKES_YOU
        )?.total_amount || 0;

      setAboutMe(user);
      setRemainingAmount(user);
      updatePremium({
        duration:
          premium?.product.subscription_details?.service_duration_days ?? 0,
        expireTime: premium?.expiry_time ?? null
      });
      setLikesCount(likesCount);
      setIsAuthenticated(true);

      void i18n.changeLanguage(user.profile.locale);

      navigate(routes.index);
    } catch (e) {
      clearTokens();
      logError(e, { loginId, context: 'Login process' }, true);
      setIsAuthenticated(false);
      catchErrors(e, true);
    } finally {
      setIsFetching(false);
    }
  };

  const connectCentrifuge = (access_token: string) => {
    centrifuge.on('connected', () => {
      setIsCentrifuge(true);
    });

    centrifuge.on('disconnected', () => {
      setIsCentrifuge(false);

      logEvent(`Disconnected from Centrifuge`, {}, 'warning', true);
    });

    centrifuge.setToken(access_token);
    centrifuge.connect();
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isFetching,
        isCentrifuge,
        login
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
